import React from 'react';
import Markdown from '@scale/frontend-lib/markdown';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import TreeView from '@material-ui/lab/TreeView';
import TreeItem, { TreeItemProps } from '@material-ui/lab/TreeItem';
import { makeStyles, Link, Checkbox, FormControlLabel, ClickAwayListener } from '@material-ui/core';
import {
  TextCollectionField,
  TextCollectionFieldType,
  FieldSet,
  TextCollectionFormParams,
  UnitFieldTypes,
  TextCollectionAttachment,
} from '@scale/shared/types/params/textcollection';
import {
  TextCollectionAnnotation,
  FieldSetAnnotation,
} from '@scale/shared/types/response/textcollection';
import { splitFieldSets, getFieldSetTitle, getFirstTextField } from './fieldSetUtils';
// @ts-ignore import JavaScript package
import pluralize from 'pluralize';
import _ from 'lodash';
import { getDefaultAnnotation } from '@scale/shared/textcollection/defaults';
import { Breadcrumb, pathJoin } from './breadcrumbs';
import { FieldSetReview, getFieldSetReview } from './review';
import classNames from 'classnames';
import FieldPreview from './FieldPreview';
import { TextCollectionDispatch } from './reducer';
import { useHighlight } from './document';
import { responseMatchesCondition } from '@scale/shared/textcollection/matcher';
import { isInlineField } from '@scale/shared/textcollection/field';

interface AnnotationTreeProps {
  fields: TextCollectionField[];
  description?: string;
  forms?: TextCollectionFormParams[];
  annotations: Record<string, TextCollectionAnnotation>;
  onRequestEdit: (breadcrumbs: Breadcrumb[]) => void;
  requestShowForm?: (formId: string) => void;
  disabled?: boolean;
  expanded: string[];
  expandAllNodes: React.MutableRefObject<string[]>;
  fieldSetReviews: Record<string, Record<number, FieldSetReview>>;
  setFieldSetReview: (breadcrumbs: Breadcrumb[], reviewed: boolean) => void;
  dispatch: TextCollectionDispatch;
  attachment: TextCollectionAttachment;
}

function AnnotationTree(props: AnnotationTreeProps) {
  const styles = useAnnotationTreeStyles();
  const highlight = useHighlight();

  // Reset reviewed nodes ref.
  props.expandAllNodes.current = [];

  // Stop propagation breaks checkbox change events, the solution is to signal the
  // native event by reference to disable the propagation.
  let skipExpandEvent: MouseEvent | null = null;
  const onNodeToggle = (event: React.ChangeEvent<any>, nodes: string[]): void => {
    if (event.nativeEvent !== skipExpandEvent) {
      props.dispatch({
        type: 'NODE-TOGGLE',
        payload: {
          nodes,
        },
      });
    }
  };

  const onClickAway = () => {
    props.dispatch({
      type: 'REVIEW-FOCUS',
      payload: {
        breadcrumbs: [],
      },
    });
  };

  interface TreeContentOptions {
    isOpen: boolean;
    parentReviewed: boolean;
  }

  const renderTreeContent = function (
    breadcrumbs: Breadcrumb[],
    fields: TextCollectionField[],
    annotations: Record<string, TextCollectionAnnotation>,
    reviews: Record<string, Record<number, FieldSetReview>>,
    { isOpen, parentReviewed }: TreeContentOptions,
  ): React.ReactNode[] {
    const [formFields, fieldSets] = splitFieldSets(fields);
    const formFieldPreviews = formFields.map(field => {
      const annotation = annotations[field.field_id] || getDefaultAnnotation(field);
      return (
        <FieldPreview
          key={field.field_id}
          field={field}
          annotation={annotation}
          onRequestEdit={() => props.onRequestEdit(breadcrumbs)}
          editDisabled={props.disabled}
          attachment={props.attachment}
        />
      );
    });
    const customFormFieldPreviews = (props.forms || [])
      .filter(form => responseMatchesCondition({ annotations: props.annotations }, form.conditions))
      .map(form =>
        form.fields
          .filter(field => UnitFieldTypes.includes(field.type))
          .map(field => {
            const annotation = annotations[field.field_id] || getDefaultAnnotation(field);
            return (
              <FieldPreview
                key={field.field_id}
                field={field}
                annotation={annotation}
                onRequestEdit={() => props.requestShowForm && props.requestShowForm(form.form_id)}
                editDisabled={props.disabled}
                attachment={props.attachment}
              />
            );
          }),
      );
    const fieldSetTreeItems: React.ReactNode[][] = fieldSets.map(fieldSet => {
      const annotation = annotations[fieldSet.field_id] || getDefaultAnnotation(fieldSet);
      if (annotation.type !== TextCollectionFieldType.FieldSet) {
        return [];
      }
      return renderTreeItems(breadcrumbs, fieldSet, annotation, reviews, {
        parentReviewed,
        isParentOpen: isOpen,
      });
    });
    const flatTreeItems: React.ReactNode[] = _.flatten(fieldSetTreeItems).filter(
      treeItem => !!treeItem,
    );
    return [..._.flatten(customFormFieldPreviews), ...formFieldPreviews, ...flatTreeItems];
  };

  interface TreeItemOptions {
    isParentOpen: boolean;
    parentReviewed: boolean;
  }

  const renderTreeItems = function (
    breadcrumbs: Breadcrumb[],
    fieldSet: FieldSet,
    annotation: FieldSetAnnotation,
    reviews: Record<string, Record<number, FieldSetReview>>,
    options: TreeItemOptions,
  ): React.ReactNode[] {
    const { isParentOpen, parentReviewed } = options;
    const treeItems = annotation.response.map((annotations, index) => {
      const title = getFieldSetTitle(fieldSet, annotations);
      const firstTextField = getFirstTextField(fieldSet);
      const nextBreadcrumb: Breadcrumb = {
        id: fieldSet.field_id,
        index,
      };
      const childBreadcrumbs = [...breadcrumbs, nextBreadcrumb];
      const id = pathJoin(childBreadcrumbs);
      const onFocus: TreeItemProps['onFocus'] = event => {
        if (event.currentTarget === event.target) {
          props.dispatch({
            type: 'REVIEW-FOCUS',
            payload: {
              breadcrumbs: childBreadcrumbs,
            },
          });
          highlight.focus(title);
        }
      };
      const onEdit = (event: React.MouseEvent<any>) => {
        skipExpandEvent = event.nativeEvent;
        props.onRequestEdit(childBreadcrumbs);
      };
      const onReviewCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        props.setFieldSetReview(childBreadcrumbs, event.target.checked);
      };
      const fieldSetReview = getFieldSetReview(reviews, nextBreadcrumb);
      const isOpen = props.expanded.includes(id);
      const reviewChecked = fieldSetReview.reviewed || parentReviewed;

      // Keep track of nodes that have not been checked off in expand all nodes reference.
      if (!reviewChecked) {
        props.expandAllNodes.current.push(id);
      }

      const onCheckboxClick = (event: React.MouseEvent<any>): void => {
        // Complicated logic. If the TreeNode is checked, after the click event propagates,
        // it will be unchecked, so this boolean is backwards. If the TreeNode will be checked
        // and closed, or not-checked and open, we want to cancel the expanded toggle.
        if (isOpen === reviewChecked) {
          skipExpandEvent = event.nativeEvent;
        }
      };
      const label = (
        <>
          <span className={classNames({ [styles.labelTextReviewed]: reviewChecked })}>{title}</span>
          {!props.disabled && (
            <Link classes={{ root: styles.editLink }} onClick={onEdit as $FIXME}>
              Edit
            </Link>
          )}
          {!props.disabled && (
            <FormControlLabel
              classes={{ root: styles.reviewLabel }}
              label="Reviewed"
              control={
                <Checkbox
                  classes={{ root: styles.reviewCheckbox }}
                  size="small"
                  checked={reviewChecked}
                  onChange={onReviewCheckboxChange}
                  onClick={onCheckboxClick}
                />
              }
            />
          )}
        </>
      );
      if (fieldSet.inline) {
        return (
          <div key={id}>
            {fieldSet.fields.map(subField => {
              if (
                isInlineField(subField) &&
                subField.hide_for_single_response &&
                annotation.response.length < 2
              ) {
                return null;
              }
              const value = annotation.response[index]?.[subField.field_id]?.response?.[0] ?? '';
              const isRequiredAndMissing = subField.required && !value;
              return (
                <div className={styles.inlineFieldSetPreview} key={id}>
                  {subField.title}:{' '}
                  {isRequiredAndMissing ? (
                    <span className={styles.missing}>Missing</span>
                  ) : (
                    (value as unknown as React.ReactNode)
                  )}
                </div>
              );
            })}
          </div>
        );
      } else {
        const childFields = fieldSet.fields.filter(field => field !== firstTextField);
        // Performance optimization: Avoid rendering nested components that are not visible.
        // TreeView will break on null children so pass empty array instead.
        const treeContent = isParentOpen
          ? renderTreeContent(
              childBreadcrumbs,
              childFields,
              annotations,
              fieldSetReview.fieldSetReviews,
              {
                isOpen,
                parentReviewed: reviewChecked,
              },
            )
          : [];
        return (
          <TreeItem
            classes={{
              group: styles.treeItemGroup,
              content: styles.treeItemContent,
              label: styles.treeItemLabel,
              iconContainer: classNames(styles.labelIcon, {
                [styles.labelIconReviewed]: reviewChecked,
                [styles.labelIconPending]: !reviewChecked,
              }),
            }}
            nodeId={id}
            key={id}
            label={label}
            expandIcon={<TriangleRight />}
            collapseIcon={<TriangleDown />}
            onFocus={onFocus}
          >
            {treeContent}
          </TreeItem>
        );
      }
    });
    const count = annotation.response.length;
    const label = pluralize(fieldSet.title.toLowerCase(), count, false);
    const suffix = count > 0 ? ':' : '';
    treeItems.unshift(
      <div key={fieldSet.field_id} className={styles.fieldSetHeading}>
        {count} {label + suffix}
      </div>,
    );
    return treeItems;
  };

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <div className={styles.sidebar}>
        {props.description && (
          <div
            className={styles.description}
            dangerouslySetInnerHTML={{
              __html: Markdown.renderInline(props.description),
            }}
          />
        )}
        <TreeView expanded={props.expanded} onNodeToggle={onNodeToggle}>
          {renderTreeContent([], props.fields, props.annotations, props.fieldSetReviews, {
            parentReviewed: false,
            isOpen: true,
          })}
        </TreeView>
      </div>
    </ClickAwayListener>
  );
}

const useAnnotationTreeStyles = makeStyles({
  sidebar: {
    flex: 1,
    padding: '20px 16px',
    overflowX: 'hidden',
    overflowY: 'scroll',
  },
  description: {
    fontWeight: 500,
    color: '#000000',
    fontSize: '16px',
    lineHeight: '24px',
    padding: '16px',
    marginTop: '8px',
  },
  fieldSetHeading: {
    fontSize: '14px',
    color: '#808080',
    lineHeight: '18px',
    margin: '6px 8px',
  },
  treeItemContent: {
    paddingLeft: '8px',
    borderRadius: '4px',
    '&:hover': {
      background: '#F8F8F8',
    },
    '&:hover $reviewLabel': {
      visibility: 'visible',
    },
    '&:hover $editLink': {
      visibility: 'visible',
    },
  },
  treeItemLabel: {
    paddingLeft: 6,
    paddingRight: 90,
    paddingTop: 6,
    paddingBottom: 6,
    fontSize: '14px',
    lineHeight: '16px',
    color: '#000000',
    fontWeight: 500,
    position: 'relative',
  },
  treeItemGroup: {
    marginLeft: '15.5px',
    paddingLeft: 8,
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 2,
    borderLeft: '1px dashed #808080',
  },
  editLink: {
    color: '#2B99D8',
    fontSize: '14px',
    lineHeight: '16px',
    marginLeft: 16,
    marginRight: 16,
    visibility: 'hidden',
  },
  reviewLabel: {
    position: 'absolute',
    top: 0,
    right: 0,
    textAlign: 'right',
    paddingTop: 1,
    paddingBottom: 1,
    paddingLeft: 8,
    paddingRight: 8,
    visibility: 'hidden',
    marginRight: 0,
    fontSize: '14px',
    lineHeight: '16px',
  },
  reviewCheckbox: {
    padding: 2,
  },
  labelIcon: {
    width: '16px',
  },
  labelIconPending: {
    color: 'rgba(0, 0, 0, 0.5)',
  },
  labelIconReviewed: {
    color: '#29CD9C',
  },
  labelTextReviewed: {
    textDecoration: 'line-through',
  },
  inlineFieldSetPreview: {
    fontSize: '14px',
    marginLeft: 8,
  },
  missing: {
    color: 'red',
  },
});

function TriangleRight() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M13.7999 9.04011C14.5999 8.58011 14.5999 7.42261 13.7999 6.96011L3.3999 0.962609C2.6024 0.502609 1.5999 1.07761 1.5999 2.00261L1.5999 14.0001C1.5999 14.9226 2.5999 15.5001 3.3999 15.0401L13.7999 9.04011Z" />
    </svg>
  );
}

function TriangleDown() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M6.95989 13.8004C7.41989 14.6004 8.57739 14.6004 9.03989 13.8004L15.0374 3.40039C15.4974 2.60289 14.9224 1.60039 13.9974 1.60039L1.99989 1.60039C1.07739 1.60039 0.499891 2.60039 0.959891 3.40039L6.95989 13.8004Z" />
    </svg>
  );
}

export default AnnotationTree;
